import { Quote } from '@client/model/quotesModel';
import { FundingPolicyType } from '@shared';
import { getMultiplierForPremium } from './lincolnPricingConfig';
import { getOAMultipliersForPremium, isOneAmericaAssetCare } from './oneAmericaPricingUtils';

const SECURIAN_MIN_DEATH_BENEFIT_CAP = 10000;
const SECURIAN_MIN_DEATH_BENEFIT_RATE = 0.1;

function calculateMinGuaranteedDeathBenefit(
  fundingPolicyType: number,
  scaledDeathBenefit: number,
  originalValue: number | undefined,
  scalingFactor: number,
): number | undefined {
  if (fundingPolicyType === FundingPolicyType.secureCareThree) {
    return Math.min(
      SECURIAN_MIN_DEATH_BENEFIT_CAP,
      scaledDeathBenefit * SECURIAN_MIN_DEATH_BENEFIT_RATE,
    );
  }
  return originalValue ? originalValue * scalingFactor : undefined;
}

function getScalingFactorsForQuote(quote: Quote, newPremium: number): { db: number; ltc: number } {
  const linearFactor = newPremium / quote.premiumAmount;

  if (quote.fundingPolicyType === FundingPolicyType.moneyGuardFixedAdvantage) {
    const originalMultiplier = getMultiplierForPremium(quote.premiumAmount);
    const newMultiplier = getMultiplierForPremium(newPremium);
    const factor = linearFactor * (newMultiplier / originalMultiplier);
    return { db: factor, ltc: factor };
  }

  if (isOneAmericaAssetCare(quote.fundingPolicyType) && (quote.usaStateCategory === 1 || quote.usaStateCategory === 3)) {
    const original = getOAMultipliersForPremium(
      quote.fundingPolicyType,
      quote.productFundingOption,
      quote.aobInflation,
      quote.cobInflation,
      quote.cobDuration,
      quote.premiumAmount,
      quote.usaStateCategory,
      quote.primaryAge,
      quote.primaryGender,
    );
    const target = getOAMultipliersForPremium(
      quote.fundingPolicyType,
      quote.productFundingOption,
      quote.aobInflation,
      quote.cobInflation,
      quote.cobDuration,
      newPremium,
      quote.usaStateCategory,
      quote.primaryAge,
      quote.primaryGender,
    );
    return {
      db: linearFactor * (target.db / original.db),
      ltc: linearFactor * (target.ltc / original.ltc),
    };
  }

  return { db: linearFactor, ltc: linearFactor };
}

function scaleValue(value: number | undefined, scalingFactor: number): number | undefined {
  return value !== undefined ? value * scalingFactor : undefined;
}

/**
 * Adjusts a quote's parameters based on a new premium amount.
 * MoneyGuard Fixed Advantage 2025 and OneAmerica AssetCare use premium band
 * correction for non-linear pricing. OneAmerica uses separate correction
 * factors for death benefit vs total LTC benefit.
 * MoneyGuard also applies a floor guarantee ensuring death benefit never
 * falls below premium. All other products use simple linear scaling.
 */
export const adjustQuoteForPremium = (originalQuote: Quote, newPremiumAmount: number): Quote => {
  if (!originalQuote.premiumAmount || originalQuote.premiumAmount <= 0) {
    return originalQuote;
  }

  const { db: dbFactor, ltc: ltcFactor } = getScalingFactorsForQuote(originalQuote, newPremiumAmount);
  let scaledDeathBenefit = originalQuote.deathBenefitInitial * dbFactor;

  if (originalQuote.fundingPolicyType === FundingPolicyType.moneyGuardFixedAdvantage) {
    scaledDeathBenefit = Math.max(scaledDeathBenefit, newPremiumAmount);
  }

  const adjustedQuote: Quote = {
    ...originalQuote,
    premiumAmount: newPremiumAmount,

    deathBenefitInitial: scaledDeathBenefit,
    deathBenefitMinimumGuaranteedIndividual: calculateMinGuaranteedDeathBenefit(
      originalQuote.fundingPolicyType,
      scaledDeathBenefit,
      originalQuote.deathBenefitMinimumGuaranteedIndividual,
      dbFactor,
    ),
    deathBenefitMinimumGuaranteedJoint: scaleValue(
      originalQuote.deathBenefitMinimumGuaranteedJoint,
      dbFactor,
    ),

    ltcBenefitMonthlyIndividual: originalQuote.ltcBenefitMonthlyIndividual * dbFactor,
    ltcBenefitAnnualIndividual: scaleValue(originalQuote.ltcBenefitAnnualIndividual, dbFactor),
    ltcBenefitTotal: originalQuote.ltcBenefitTotal * ltcFactor,
    maxMonthlyLTCBenefitReimbursement: scaleValue(
      originalQuote.maxMonthlyLTCBenefitReimbursement,
      dbFactor,
    ),
    maxMonthlyLTCBenefitIndemnity: scaleValue(
      originalQuote.maxMonthlyLTCBenefitIndemnity,
      dbFactor,
    ),

    ltcBenefitDurationMonths: originalQuote.ltcBenefitDurationMonths,
    investmentMin: originalQuote.investmentMin,
    investmentMax: originalQuote.investmentMax,

    projectedCostCoverage: undefined,
    roi: undefined,
    roiValues: undefined,
    formattedROI: undefined,
  };

  return adjustedQuote;
};
export const adjustQuotesForPremium = (
  originalQuotes: Quote[],
  newPremiumAmount: number,
): Quote[] => originalQuotes.map(quote => adjustQuoteForPremium(quote, newPremiumAmount));

/**
 * Adjusts an EquiTrust quote's LTC benefits based on a new coverage ratio
 * LTC benefits scale proportionally with coverage ratio changes
 * Premium and death benefit remain unchanged
 */
export const adjustQuoteForCoverageRatio = (
  originalQuote: Quote,
  newCoverageRatio: number,
): Quote => {
  const originalCoverageRatio = originalQuote.coverageRatio;
  if (!originalCoverageRatio || originalCoverageRatio <= 0) {
    return originalQuote;
  }

  const minRatio = originalQuote.minCoverageRatio || originalCoverageRatio;
  const maxRatio = originalQuote.maxCoverageRatio || originalCoverageRatio;
  const clampedRatio = Math.max(minRatio, Math.min(maxRatio, newCoverageRatio));

  const scalingFactor = clampedRatio / originalCoverageRatio;

  return {
    ...originalQuote,
    coverageRatio: clampedRatio,
    ltcBenefitMonthlyIndividual: originalQuote.ltcBenefitMonthlyIndividual * scalingFactor,
    ltcBenefitAnnualIndividual: scaleValue(
      originalQuote.ltcBenefitAnnualIndividual,
      scalingFactor,
    ),
    ltcBenefitTotal: originalQuote.ltcBenefitTotal * scalingFactor,
    projectedCostCoverage: undefined,
    roi: undefined,
    roiValues: undefined,
    formattedROI: undefined,
  };
};
